/**
 * AdminMetricsPage — Track A observability dashboard.
 *
 * Shows four panels (Health / Activity / Cost / Profitability) populated by
 * GET /api/admin/metrics?range=7d|30d|all.
 *
 * Auto-refreshes every 60 s. The backend caches the same response for 60 s so
 * back-to-back refreshes hit memory, not the database.
 *
 * Admin role only. Non-admin users should never reach this route because
 * AdminGuard blocks the route in App.tsx.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  ArrowPathIcon,
  HeartIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  BanknotesIcon,
  ScaleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { getMetrics } from '@/services/adminApi';
import { Skeleton } from '@/components/ui';
import type {
  MetricsRange,
  MetricsResponse,
  HealthPanel,
  ActivityPanel,
  CostPanel,
  ProfitabilityPanel,
  TierSeatUtilization,
} from '@/types/admin';

// ─── Constants ────────────────────────────────────────────────────────────────

const REFRESH_INTERVAL_MS = 60_000;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCents(cents: number): string {
  const dollars = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(dollars);
}

function formatPct(value: number): string {
  return `${value.toFixed(1)}%`;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(Math.round(value));
}

function formatSeconds(seconds: number): string {
  if (seconds === 0) return '—';
  if (seconds < 1) return `${Math.round(seconds * 1000)} ms`;
  return `${seconds.toFixed(1)} s`;
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface StatRowProps {
  label: string;
  value: string;
  tooltip?: string;
  highlight?: 'good' | 'warn' | 'bad';
}

function StatRow({ label, value, tooltip, highlight }: StatRowProps) {
  const highlightColor =
    highlight === 'good'
      ? 'var(--color-success, #4ade80)'
      : highlight === 'warn'
        ? 'var(--color-warning, #facc15)'
        : highlight === 'bad'
          ? 'var(--color-danger, #f87171)'
          : 'var(--color-text-primary)';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 0',
        borderBottom: '1px solid var(--color-border-subtle)',
      }}
      title={tooltip}
    >
      <span
        style={{
          fontSize: '13px',
          color: 'var(--color-text-secondary)',
          flexShrink: 0,
          marginRight: '12px',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: '15px',
          fontWeight: 600,
          color: highlightColor,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </span>
    </div>
  );
}

interface PanelCardProps {
  title: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  children: React.ReactNode;
}

function PanelCard({ title, icon: Icon, children }: PanelCardProps) {
  return (
    <div
      style={{
        backgroundColor: 'var(--color-dark-elevated-1)',
        border: '1px solid var(--color-border-subtle)',
        borderRadius: '14px',
        padding: '20px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '12px',
        }}
      >
        <Icon
          style={{ width: '18px', height: '18px', color: 'var(--color-accent-gold)', flexShrink: 0 }}
          aria-hidden="true"
        />
        <span
          style={{
            fontSize: '12px',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'var(--color-text-muted)',
          }}
        >
          {title}
        </span>
      </div>
      {children}
    </div>
  );
}

// ─── Panel renderers ──────────────────────────────────────────────────────────

function HealthPanelView({ panel }: { panel: HealthPanel }) {
  return (
    <PanelCard title="Health" icon={HeartIcon}>
      <StatRow
        label="Eval success rate (24h)"
        value={formatPct(panel.eval_success_rate_pct)}
        highlight={
          panel.eval_success_rate_pct >= 98
            ? 'good'
            : panel.eval_success_rate_pct >= 95
              ? 'warn'
              : 'bad'
        }
        tooltip="Fraction of evaluation jobs that completed without error in the past 24 hours"
      />
      <StatRow
        label="Queue depth (now)"
        value={formatNumber(panel.eval_queue_depth_now)}
        highlight={
          panel.eval_queue_depth_now === 0
            ? 'good'
            : panel.eval_queue_depth_now <= 5
              ? 'warn'
              : 'bad'
        }
        tooltip="Number of evaluation jobs currently queued or in-flight"
      />
      <StatRow
        label="5xx rate"
        value={formatPct(panel.api_5xx_rate_pct)}
        highlight={
          panel.api_5xx_rate_pct === 0
            ? 'good'
            : panel.api_5xx_rate_pct < 1
              ? 'warn'
              : 'bad'
        }
        tooltip="Fraction of API requests that returned a 5xx status in the selected period"
      />
      <StatRow
        label="Eval p95 latency"
        value={formatSeconds(panel.eval_p95_latency_seconds)}
        highlight={
          panel.eval_p95_latency_seconds === 0
            ? undefined
            : panel.eval_p95_latency_seconds < 30
              ? 'good'
              : panel.eval_p95_latency_seconds < 90
                ? 'warn'
                : 'bad'
        }
        tooltip="95th-percentile processing_time_ms for evaluations in the last 24 hours"
      />
      <StatRow
        label="Last successful eval"
        value={formatTimestamp(panel.last_successful_eval_at)}
        tooltip="Timestamp of the most recently completed evaluation job"
      />
    </PanelCard>
  );
}

function ActivityPanelView({ panel }: { panel: ActivityPanel }) {
  return (
    <PanelCard title="Activity" icon={UserGroupIcon}>
      <StatRow
        label="Active users (7d)"
        value={formatNumber(panel.active_users_7d)}
        tooltip="Distinct users who triggered at least one evaluation in the past 7 days"
      />
      <StatRow
        label="Active users (30d)"
        value={formatNumber(panel.active_users_30d)}
        tooltip="Distinct users who triggered at least one evaluation in the past 30 days"
      />
      <StatRow
        label="Evals submitted"
        value={formatNumber(panel.evals_submitted_period)}
        tooltip="Total evaluations submitted in the selected period"
      />
      <StatRow
        label="New signups"
        value={formatNumber(panel.new_signups_period)}
        tooltip="User accounts created in the selected period"
      />
      <StatRow
        label="Templates created"
        value={formatNumber(panel.templates_created_period)}
        tooltip="New movement templates created in the selected period"
      />
      {panel.top_templates_by_eval_count.length > 0 && (
        <div style={{ marginTop: '8px' }}>
          <span
            style={{
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--color-text-muted)',
              display: 'block',
              marginBottom: '6px',
            }}
          >
            Top templates
          </span>
          {panel.top_templates_by_eval_count.slice(0, 3).map((t) => (
            <div
              key={t.template_id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '6px 0',
                borderBottom: '1px solid var(--color-border-subtle)',
              }}
            >
              <span
                style={{
                  fontSize: '12px',
                  color: 'var(--color-text-secondary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '70%',
                }}
                title={t.template_name}
              >
                {t.template_name}
              </span>
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'var(--color-text-primary)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatNumber(t.eval_count)} evals
              </span>
            </div>
          ))}
        </div>
      )}
    </PanelCard>
  );
}

function CostPanelView({ panel }: { panel: CostPanel }) {
  return (
    <PanelCard title="Cost" icon={CurrencyDollarIcon}>
      <StatRow
        label="LLM spend (period)"
        value={formatCents(panel.llm_cost_usd_cents_period)}
        tooltip="Internal estimate of LLM API spend. Verify against OpenAI and Gemini dashboards."
      />
      <StatRow
        label="LLM cost / eval (avg)"
        value={formatCents(panel.llm_cost_per_eval_avg_cents)}
        tooltip="Average LLM cost per evaluation in the selected period"
      />
      <StatRow
        label="Eval count (period)"
        value={formatNumber(panel.eval_count_period)}
        tooltip="Total evaluations in the selected period"
      />
      <StatRow
        label="Storage total"
        value={`${panel.storage_total_gb.toFixed(2)} GB`}
        tooltip="Estimated total storage used (evaluations_index JSON size heuristic)"
      />
      <StatRow
        label="Est. monthly total"
        value={formatCents(panel.estimated_monthly_total_cents)}
        tooltip="Extrapolated monthly LLM cost based on current-period daily rate"
      />
      <StatRow
        label="LLM cost / min of video"
        value={
          panel.llm_cost_per_minute_cents !== null
            ? formatCents(panel.llm_cost_per_minute_cents)
            : '—'
        }
        tooltip="Internal estimate of LLM cost per minute of scored video. Null for windows with no duration data (legacy rows). Not billing-grade."
      />
    </PanelCard>
  );
}

// ─── Membership / MRR panel ──────────────────────────────────────────────────

function MembershipMrrPanel({ panel }: { panel: ProfitabilityPanel }) {
  const chartData = panel.subscription_mrr_by_tier.map((row) => ({
    name: row.label,
    mrr: row.mrr_cents / 100,
    channels: row.active_channel_count,
  }));

  // Assign a distinct accent per tier slot (up to 3 tiers)
  const BAR_COLORS = [
    'rgba(61,169,252,0.82)',   // tier 1
    'rgba(30,215,96,0.80)',    // tier 2
    'rgba(244,201,93,0.82)',   // tier 3
  ];

  return (
    <PanelCard title="Membership / MRR" icon={ChartBarIcon}>
      {/* Summary row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '10px',
          marginBottom: '16px',
        }}
      >
        {[
          {
            label: 'Total subscription MRR',
            value: formatCents(panel.total_subscription_mrr_cents),
            accent: panel.total_subscription_mrr_cents > 0 ? 'var(--color-success, #4ade80)' : undefined,
          },
          {
            label: 'Active subscribed channels',
            value: formatNumber(panel.active_subscribed_channels),
            accent: undefined,
          },
          {
            label: 'ARPA per channel',
            value: panel.arpa_cents !== null ? formatCents(panel.arpa_cents) : '—',
            accent: undefined,
          },
        ].map(({ label, value, accent }) => (
          <div
            key={label}
            style={{
              padding: '10px 12px',
              borderRadius: '10px',
              backgroundColor: 'var(--color-dark-elevated-2)',
              border: '1px solid var(--color-border-subtle)',
            }}
          >
            <span
              style={{
                display: 'block',
                fontSize: '10px',
                fontWeight: 600,
                color: 'var(--color-text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                marginBottom: '4px',
              }}
            >
              {label}
            </span>
            <span
              style={{
                fontSize: '16px',
                fontWeight: 700,
                color: accent ?? 'var(--color-text-primary)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {value}
            </span>
          </div>
        ))}
      </div>

      {/* MRR by tier bar chart */}
      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={160}>
          <BarChart
            data={chartData}
            margin={{ top: 4, right: 4, bottom: 0, left: -8 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="var(--color-border-subtle)"
              vertical={false}
            />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `$${(v as number).toLocaleString()}`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--color-dark-elevated-2)',
                border: '1px solid var(--color-border-subtle)',
                borderRadius: '8px',
                fontSize: '12px',
                color: 'var(--color-text-primary)',
              }}
              cursor={{ fill: 'rgba(255,255,255,0.04)' }}
              formatter={(value, _name, props) => {
                const channels = (props as { payload?: { channels: number } }).payload?.channels ?? 0;
                return [`${formatCents((value as number) * 100)} (${channels} channels)`, 'MRR'];
              }}
            />
            <Bar dataKey="mrr" radius={[4, 4, 0, 0]} maxBarSize={60}>
              {chartData.map((_entry, idx) => (
                <Cell key={idx} fill={BAR_COLORS[idx % BAR_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div
          style={{
            padding: '24px 0',
            textAlign: 'center',
            color: 'var(--color-text-muted)',
            fontSize: '13px',
          }}
        >
          No subscription tiers active yet
        </div>
      )}

      {/* User conversion stats */}
      <div style={{ marginTop: '12px' }}>
        <StatRow
          label="Paid users"
          value={formatNumber(panel.paid_users_count)}
          tooltip="Active users on the paid tier"
        />
        <StatRow
          label="Free users"
          value={formatNumber(panel.free_users_count)}
          tooltip="Active users on the free tier"
        />
        <StatRow
          label="Free-to-paid conversion"
          value={formatPct(panel.free_to_paid_conversion_pct)}
          highlight={
            panel.free_to_paid_conversion_pct >= 5
              ? 'good'
              : panel.free_to_paid_conversion_pct >= 2
                ? 'warn'
                : undefined
          }
          tooltip="Paid users as a percentage of total active users"
        />
        <StatRow
          label="Evals / paid user (30d avg)"
          value={panel.evals_per_paid_user_avg_30d.toFixed(1)}
          tooltip="Average evaluations per paid user in the last 30 days"
        />
      </div>
    </PanelCard>
  );
}

// ─── Token revenue panel ──────────────────────────────────────────────────────

function TokenRevenuePanelView({ panel }: { panel: ProfitabilityPanel }) {
  return (
    <PanelCard title="Token Revenue" icon={BanknotesIcon}>
      {!panel.economy_active && (
        <div
          data-testid="economy-inactive-note"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            marginBottom: '12px',
            borderRadius: '8px',
            backgroundColor: 'rgba(250,204,21,0.07)',
            border: '1px solid rgba(250,204,21,0.2)',
          }}
        >
          <ClockIcon
            style={{ width: '14px', height: '14px', flexShrink: 0, color: 'var(--color-warning, #facc15)' }}
            aria-hidden="true"
          />
          <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
            Token economy not yet enabled — revenue will appear here once activated
          </span>
        </div>
      )}
      <StatRow
        label="Token revenue (period)"
        value={formatCents(panel.token_revenue_period_cents)}
        tooltip="Token purchases in the selected period"
      />
      <StatRow
        label="Token revenue (30d)"
        value={formatCents(panel.token_revenue_30d_cents)}
        tooltip="Token purchases in the last 30 days"
      />
      <StatRow
        label="Breakage (unused included credits)"
        value={formatNumber(panel.breakage_cents)}
        tooltip="Voided included-bucket credits in the period. This is a credit count, not a dollar amount. Zero when economy is inactive."
      />
    </PanelCard>
  );
}

// ─── Margin panel ─────────────────────────────────────────────────────────────

function MarginPanelView({ panel }: { panel: ProfitabilityPanel }) {
  const totalRevenueCents =
    panel.total_subscription_mrr_cents + panel.token_revenue_30d_cents;
  const totalCogsCents =
    panel.cogs_llm_period_cents + panel.cogs_infra_monthly_cents;

  return (
    <PanelCard title="Margin" icon={ScaleIcon}>
      <StatRow
        label="Total revenue (MRR + 30d tokens)"
        value={formatCents(totalRevenueCents)}
        highlight={totalRevenueCents > 0 ? 'good' : undefined}
        tooltip="Subscription MRR plus token revenue over the last 30 days"
      />
      <StatRow
        label="COGS — LLM (period)"
        value={formatCents(panel.cogs_llm_period_cents)}
        tooltip="Estimated LLM API cost in the selected period"
      />
      <StatRow
        label="COGS — infra (monthly est.)"
        value={formatCents(panel.cogs_infra_monthly_cents)}
        tooltip="Fixed monthly infrastructure cost estimate ($18.00)"
      />
      <StatRow
        label="Total COGS"
        value={formatCents(totalCogsCents)}
        tooltip="LLM + infrastructure cost combined"
      />
      <StatRow
        label="Gross margin"
        value={panel.gross_margin_pct !== null ? formatPct(panel.gross_margin_pct) : '—'}
        highlight={
          panel.gross_margin_pct === null
            ? undefined
            : panel.gross_margin_pct >= 60
              ? 'good'
              : panel.gross_margin_pct >= 30
                ? 'warn'
                : 'bad'
        }
        tooltip="(Revenue − COGS) / Revenue. Shows '—' when revenue is zero."
      />
    </PanelCard>
  );
}

// ─── Seat utilization panel ──────────────────────────────────────────────────

function SeatUtilizationPanel({ panel }: { panel: ProfitabilityPanel }) {
  const { seat_utilization: su } = panel;

  const platformPct =
    su.platform_utilization_pct !== null
      ? `${su.platform_utilization_pct.toFixed(1)}%`
      : '—';

  return (
    <PanelCard title="Seat Utilization" icon={UserGroupIcon}>
      {/* Platform totals */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '10px',
          marginBottom: '16px',
        }}
      >
        {[
          { label: 'Seats consumed', value: formatNumber(su.total_seats_consumed) },
          { label: 'Seats granted', value: formatNumber(su.total_seats_granted) },
          { label: 'Platform utilization', value: platformPct },
        ].map(({ label, value }) => (
          <div
            key={label}
            style={{
              padding: '10px 12px',
              borderRadius: '10px',
              backgroundColor: 'var(--color-dark-elevated-2)',
              border: '1px solid var(--color-border-subtle)',
            }}
          >
            <span
              style={{
                display: 'block',
                fontSize: '10px',
                fontWeight: 600,
                color: 'var(--color-text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                marginBottom: '4px',
              }}
            >
              {label}
            </span>
            <span
              style={{
                fontSize: '16px',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {value}
            </span>
          </div>
        ))}
      </div>

      {/* Per-tier table */}
      {su.by_tier.length > 0 ? (
        <div>
          {/* Header row */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 80px 80px 80px',
              gap: '8px',
              padding: '6px 0',
              borderBottom: '1px solid var(--color-border-subtle)',
            }}
          >
            {['Tier', 'Channels', 'Used / Granted', 'Utilization'].map((h) => (
              <span
                key={h}
                style={{
                  fontSize: '10px',
                  fontWeight: 600,
                  color: 'var(--color-text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  textAlign: h === 'Tier' ? 'left' : 'right',
                }}
              >
                {h}
              </span>
            ))}
          </div>
          {su.by_tier.map((row: TierSeatUtilization) => (
            <div
              key={row.tier}
              data-testid={`seat-util-tier-${row.tier}`}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 80px 80px 80px',
                gap: '8px',
                padding: '9px 0',
                borderBottom: '1px solid var(--color-border-subtle)',
                alignItems: 'center',
              }}
            >
              <span style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                {row.label}
              </span>
              <span
                style={{
                  fontSize: '13px',
                  fontWeight: 500,
                  color: 'var(--color-text-primary)',
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatNumber(row.active_channel_count)}
              </span>
              <span
                style={{
                  fontSize: '13px',
                  fontWeight: 500,
                  color: 'var(--color-text-primary)',
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatNumber(row.seats_consumed)}/{formatNumber(row.seats_granted)}
              </span>
              <span
                style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color:
                    row.utilization_pct === null
                      ? 'var(--color-text-muted)'
                      : row.utilization_pct >= 80
                        ? 'var(--color-success, #4ade80)'
                        : row.utilization_pct >= 50
                          ? 'var(--color-warning, #facc15)'
                          : 'var(--color-text-secondary)',
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {row.utilization_pct !== null ? `${row.utilization_pct.toFixed(1)}%` : '—'}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div
          style={{
            padding: '20px 0',
            textAlign: 'center',
            color: 'var(--color-text-muted)',
            fontSize: '13px',
          }}
        >
          No seat data available for this window
        </div>
      )}
    </PanelCard>
  );
}

// ─── Coming-soon KPIs panel ───────────────────────────────────────────────────

const COMING_SOON_KPIS: { label: string; tooltip: string }[] = [
  { label: 'Churn rate', tooltip: 'Monthly channel churn — not yet instrumented' },
  { label: 'CAC', tooltip: 'Customer acquisition cost — not yet instrumented' },
  { label: 'NRR', tooltip: 'Net revenue retention — not yet instrumented' },
  { label: 'LTV', tooltip: 'Lifetime value per channel — not yet instrumented' },
];

function ComingSoonKpiPanel() {
  return (
    <PanelCard title="Roadmap KPIs" icon={ClockIcon}>
      <p
        style={{
          fontSize: '12px',
          color: 'var(--color-text-muted)',
          margin: '0 0 12px',
          lineHeight: 1.5,
        }}
      >
        These metrics are planned but not yet instrumented. They appear here to document the
        roadmap without showing misleading $0 values.
      </p>
      {COMING_SOON_KPIS.map(({ label, tooltip }) => (
        <div
          key={label}
          title={tooltip}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '9px 0',
            borderBottom: '1px solid var(--color-border-subtle)',
          }}
        >
          <span style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>
            {label}
          </span>
          <span
            data-testid={`coming-soon-${label.toLowerCase().replace(/\s+/g, '-')}`}
            style={{
              fontSize: '12px',
              fontWeight: 500,
              color: 'var(--color-text-muted)',
              fontStyle: 'italic',
            }}
          >
            Not yet tracked
          </span>
        </div>
      ))}
    </PanelCard>
  );
}

// ─── Legacy profitability panel (now replaced by the four panels above) ───────
// Kept as a type-check stub; not rendered.
function ProfitabilityPanelView({ panel }: { panel: ProfitabilityPanel }) {
  // This component is superseded by the four financial panels below. Keeping
  // the signature so that TypeScript validates ProfitabilityPanel usage here.
  void panel;
  return null;
}

// ─── Range selector ───────────────────────────────────────────────────────────

const RANGE_OPTIONS: { value: MetricsRange; label: string }[] = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: 'all', label: 'All time' },
];

interface RangeSelectorProps {
  value: MetricsRange;
  onChange: (range: MetricsRange) => void;
  disabled?: boolean;
}

function RangeSelector({ value, onChange, disabled }: RangeSelectorProps) {
  return (
    <div
      style={{
        display: 'flex',
        gap: '4px',
        backgroundColor: 'var(--color-dark-elevated-2)',
        padding: '4px',
        borderRadius: '10px',
        border: '1px solid var(--color-border-subtle)',
      }}
      role="group"
      aria-label="Metrics time range"
    >
      {RANGE_OPTIONS.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            style={{
              padding: '6px 16px',
              borderRadius: '7px',
              border: 'none',
              cursor: disabled ? 'not-allowed' : 'pointer',
              fontWeight: active ? 600 : 400,
              fontSize: '13px',
              transition: 'background-color 120ms ease, color 120ms ease',
              backgroundColor: active ? 'var(--color-dark-elevated-1)' : 'transparent',
              color: active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
              opacity: disabled ? 0.5 : 1,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Skeleton loader ──────────────────────────────────────────────────────────
// Uses the shared Skeleton component from ui/ for consistency with the rest of
// the app (same shimmer token: bg-dark-elevated-2 + animate-pulse).

function SkeletonPanel() {
  return (
    <div
      style={{
        backgroundColor: 'var(--color-dark-elevated-1)',
        border: '1px solid var(--color-border-subtle)',
        borderRadius: '14px',
        padding: '20px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
      }}
      aria-busy="true"
      aria-label="Loading metrics"
    >
      {/* Icon + title row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <Skeleton className="h-[18px] w-[18px] rounded" />
        <Skeleton className="h-3 w-24 rounded" />
      </div>
      {/* Stat rows — alternating widths mimic the real stat rows */}
      {(['80%', '60%', '75%', '55%', '70%'] as const).map((w, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Skeleton className="h-3 rounded" width={w} />
          <Skeleton className="h-4 rounded" width="56px" />
        </div>
      ))}
    </div>
  );
}

// ─── Main page component ──────────────────────────────────────────────────────

function AdminMetricsPage() {
  const [range, setRange] = useState<MetricsRange>('7d');
  const [data, setData] = useState<MetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchMetrics = useCallback(
    async (selectedRange: MetricsRange) => {
      try {
        const response = await getMetrics(selectedRange);
        setData(response);
        setError(null);
        setLastUpdated(new Date());
      } catch {
        setError('Failed to load metrics. Check that the backend is running.');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Fetch when range changes
  useEffect(() => {
    setLoading(true);
    fetchMetrics(range);
  }, [range, fetchMetrics]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      fetchMetrics(range);
    }, REFRESH_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [range, fetchMetrics]);

  function handleRangeChange(newRange: MetricsRange) {
    setRange(newRange);
  }

  function handleManualRefresh() {
    setLoading(true);
    fetchMetrics(range);
    // Reset the interval timer so it counts 60s from this refresh
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      fetchMetrics(range);
    }, REFRESH_INTERVAL_MS);
  }

  return (
    <div
      style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '32px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
      }}
    >
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1
            style={{
              fontSize: '20px',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
              margin: 0,
            }}
          >
            Platform Metrics
          </h1>
          {lastUpdated && (
            <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', margin: '4px 0 0' }}>
              Updated {lastUpdated.toLocaleTimeString()} · auto-refreshes every 60 s
            </p>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <RangeSelector value={range} onChange={handleRangeChange} disabled={loading} />
          <button
            type="button"
            onClick={handleManualRefresh}
            disabled={loading}
            aria-label="Refresh metrics"
            title="Refresh metrics"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              border: '1px solid var(--color-border-subtle)',
              backgroundColor: 'var(--color-dark-elevated-1)',
              color: 'var(--color-text-secondary)',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.5 : 1,
            }}
          >
            <ArrowPathIcon
              style={{
                width: '16px',
                height: '16px',
                animation: loading ? 'spin 1s linear infinite' : 'none',
              }}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && !loading && (
        <div
          role="alert"
          style={{
            backgroundColor: 'rgba(248,113,113,0.1)',
            border: '1px solid rgba(248,113,113,0.3)',
            borderRadius: '10px',
            padding: '12px 16px',
            color: 'var(--color-danger, #f87171)',
            fontSize: '14px',
          }}
        >
          {error}
        </div>
      )}

      {/* Operational panels — 2-column grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(480px, 1fr))',
          gap: '20px',
        }}
      >
        {loading || !data ? (
          <>
            <SkeletonPanel />
            <SkeletonPanel />
            <SkeletonPanel />
            <SkeletonPanel />
          </>
        ) : (
          <>
            <HealthPanelView panel={data.health} />
            <ActivityPanelView panel={data.activity} />
            <CostPanelView panel={data.cost} />
            {/* Legacy component kept for TS type-safety; not visible */}
            <ProfitabilityPanelView panel={data.profitability} />
          </>
        )}
      </div>

      {/* Financial panels — span full width then 2-col */}
      {data && !loading && (
        <>
          <h2
            style={{
              fontSize: '13px',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--color-text-muted)',
              margin: '8px 0 0',
            }}
          >
            Financial
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(480px, 1fr))',
              gap: '20px',
            }}
          >
            <MembershipMrrPanel panel={data.profitability} />
            <TokenRevenuePanelView panel={data.profitability} />
            <SeatUtilizationPanel panel={data.profitability} />
            <MarginPanelView panel={data.profitability} />
            <ComingSoonKpiPanel />
          </div>
        </>
      )}

      {/* Cost disclaimer */}
      {data && !loading && (
        <p
          style={{
            fontSize: '11px',
            color: 'var(--color-text-muted)',
            margin: 0,
            lineHeight: 1.5,
          }}
        >
          LLM cost figures are internal estimates derived from a rate card and are NOT billing-grade.
          Always verify against the{' '}
          <a
            href="https://platform.openai.com/usage"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--color-accent)' }}
          >
            OpenAI dashboard
          </a>{' '}
          and{' '}
          <a
            href="https://aistudio.google.com/app/usage"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--color-accent)' }}
          >
            Google AI dashboard
          </a>
          .
        </p>
      )}
    </div>
  );
}

export default AdminMetricsPage;
